import React, { useEffect, useMemo, useState } from 'react';
import { API } from 'aws-amplify';
import ProductCard from '../components/ProductCard';
import ProductDetailsModal from '../components/ProductDetailsModal';
import { useCart } from '../components/CartContext';
import mockProducts from '../mockProducts.json';

function AccessoriesCollectibles() {
  const [allProducts, setAllProducts] = useState([]);       // raw list (Accessory + not sold)
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [sortOrder, setSortOrder] = useState('asc');        // 'asc' | 'desc'
  const [nameFilter, setNameFilter] = useState('All');      // product name or 'All'
  const { addToCart } = useCart();

  // Fetch once, store the raw (unsorted) list
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await API.get('apiNtrepid', '/items', {
          queryStringParameters: { type: 'Accessory' },
        });
        const items = response.items || response;
        const accessoryProducts = (items || []).filter(
          (item) => item.type === 'Accessory' && item.sold !== true
        );
        setAllProducts(accessoryProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
        const mockAccessory = (mockProducts || []).filter(
          (item) => item.type === 'Accessory' && item.sold !== true
        );
        setAllProducts(mockAccessory);
      }
    };

    fetchProducts();
  }, []);

  // Distinct product names for the dropdown
  const productNames = useMemo(() => {
    const names = Array.from(new Set(allProducts.map((p) => p.name))).sort((a, b) =>
      a.localeCompare(b)
    );
    return names;
  }, [allProducts]);

  // Apply name filter, then sort
  const products = useMemo(() => {
    const base =
      nameFilter === 'All'
        ? allProducts
        : allProducts.filter((p) => p.name === nameFilter);

    const sorted = [...base].sort((a, b) =>
      sortOrder === 'asc'
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name)
    );
    return sorted;
  }, [allProducts, nameFilter, sortOrder]);

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedProduct(null);
  };

  return (
    <div className="container px-4 px-lg-5 mt-5 flex-grow-1">
      {/* Controls row */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div className="d-flex align-items-center gap-2">
          <label htmlFor="nameFilter" className="form-label mb-0">
            Product
          </label>
          <select
            id="nameFilter"
            value={nameFilter}
            onChange={(e) => setNameFilter(e.target.value)}
            className="form-select"
          >
            <option value="All">All products</option>
            {productNames.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <div className="d-flex align-items-center gap-2">
          <label htmlFor="sortOrder" className="form-label mb-0">
            Sort
          </label>
          <select
            id="sortOrder"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="form-select w-auto"
          >
            <option value="asc">Name (A–Z)</option>
            <option value="desc">Name (Z–A)</option>
          </select>
        </div>

        <div className="text-muted ms-auto">
          {products.length} item{products.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Grid */}
      <div className="row gx-4 gx-lg-5 row-cols-2 row-cols-md-3 row-cols-xl-4 justify-content-center">
        {products.map((product) => (
          <ProductCard key={product.id} product={product} onViewDetails={handleViewDetails} />
        ))}
      </div>

      {/* Modal */}
      <ProductDetailsModal
        show={showModal}
        handleClose={handleCloseModal}
        product={selectedProduct}
        addToCart={addToCart}
      />
    </div>
  );
}

export default AccessoriesCollectibles;
